import { useState, useEffect } from 'react';
import { Platform } from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { AuthAPI } from '@/services/residents/api/AuthAPI';
import { AuthStackParamList } from '@/navigation/residents/mobile/AuthNavigator';
import { isPasswordValid, getPasswordRequirements } from '@/utils/residents/validation';

type ResetPasswordRouteProp = RouteProp<AuthStackParamList, 'ResetPassword'>;

// Declare window for web platform
declare const window: any;

export interface UseResetPasswordFormReturn {
  // Form state
  password: string;
  confirmPassword: string;
  isLoading: boolean;
  formError: string | null;
  token: string;
  
  // Password visibility
  showPassword: boolean;
  showConfirmPassword: boolean;
  
  // Actions
  setPassword: (value: string) => void;
  setConfirmPassword: (value: string) => void;
  setShowPassword: (value: boolean) => void;
  setShowConfirmPassword: (value: boolean) => void;
  toggleShowPassword: () => void;
  toggleShowConfirmPassword: () => void;
  handleSubmit: () => Promise<void>;
  handleCancel: () => void;
  
  // Computed
  passwordRequirements: { min: number; max: number };
  isFormValid: boolean;
}

export const useResetPasswordForm = (
  onError?: (message: string) => void,
  onSuccess?: (message: string) => void
): UseResetPasswordFormReturn => {
  const navigation = useNavigation<any>();
  const route = useRoute<ResetPasswordRouteProp>();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [token, setToken] = useState<string>('');
  
  // Password visibility states
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Extract token from URL or route params
  useEffect(() => {
    let tokenFromUrl = route.params?.token;
    
    // On web, also check URL query parameters
    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined' && window.location) {
        const params = new URLSearchParams(window.location.search);
        const urlToken = params.get('token');
        if (urlToken) {
          tokenFromUrl = urlToken;
        }
      }
    }
    
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    } else {
      const errorMsg = 'Invalid or missing reset token. Please request a new password reset.';
      setFormError(errorMsg);
      if (onError) {
        onError(errorMsg);
      }
      // Delay navigation to allow error to be seen
      setTimeout(() => {
        navigation.navigate('Login');
      }, 3000);
    }
  }, [route.params?.token, navigation, onError]);

  const validateForm = (): string | null => {
    if (!password) {
      return 'Please enter a password.';
    }
    
    if (!isPasswordValid(password)) {
      const { min, max } = getPasswordRequirements();
      return `Password must be between ${min} and ${max} characters.`;
    }
    
    if (password !== confirmPassword) {
      return 'Passwords do not match.';
    }
    
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      if (onError && Platform.OS !== 'web') {
        // On native, show alert; on web, the form error is displayed inline
        onError(validationError);
      }
      return;
    }
    
    if (!token) {
      const errorMsg = 'Invalid reset token. Please request a new password reset.';
      setFormError(errorMsg);
      if (onError) {
        onError(errorMsg);
      }
      return;
    }

    setIsLoading(true);
    setFormError(null);
    
    try {
      const response = await AuthAPI.resetPassword(token, password);
      
      if (response.success && response.response?.user && response.response?.accessToken) {
        const successMsg = 'Password reset successful! Please login with your new password.';
        if (onSuccess) {
          onSuccess(successMsg);
        }
        
        // Redirect to login screen
        setTimeout(() => {
          navigation.navigate('Login');
        }, Platform.OS === 'web' ? 2000 : 0);
      } else {
        const errorMsg = 'Failed to reset password. Please try again.';
        setFormError(errorMsg);
        if (onError) {
          onError(errorMsg);
        }
      }
    } catch (error: any) {
      const errorMessage = error.message || 'An error occurred while resetting your password. Please try again or request a new reset link.';
      setFormError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigation.navigate('Login');
  };

  const toggleShowPassword = () => setShowPassword(!showPassword);
  const toggleShowConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

  const passwordRequirements = getPasswordRequirements();
  const isFormValid = !!password && !!confirmPassword && password === confirmPassword && isPasswordValid(password);

  return {
    // Form state
    password,
    confirmPassword,
    isLoading,
    formError,
    token,
    
    // Password visibility
    showPassword,
    showConfirmPassword,
    
    // Actions
    setPassword,
    setConfirmPassword,
    setShowPassword,
    setShowConfirmPassword,
    toggleShowPassword,
    toggleShowConfirmPassword,
    handleSubmit,
    handleCancel,
    
    // Computed
    passwordRequirements,
    isFormValid,
  };
};

